<template>
    <div
        class="layout__main layout__main--no-top-padding layout__main--double"
        :class="{
            'layout__main--sidebar': isDesktop,
        }"
    >
        <BaseSection class="container-narrow layout__hero">
            <template #breadcrumbs>
                <span>
                    <template v-if="chapterTitle">
                        {{ chapterTitle }}
                    </template>
                    <template v-else>
                        <ContentPlaceholdersText :lines="1" />
                    </template>
                </span>
            </template>
            <template #title>
                <h1>
                    <template v-if="moduleItem?.title">
                        {{ moduleItem?.title }}
                    </template>
                    <template v-else>
                        <ContentPlaceholdersText :lines="1" />
                    </template>
                </h1>
            </template>
            <template #description>
                <template v-if="moduleItem?.timeEstimate">
                    <BaseTimeEstimate :time="`${ moduleItem?.timeEstimate } min`" />
                </template>
            </template>
        </BaseSection>

        <article class="container-narrow">
            <TheAsideLinks
                v-if="isDesktop && items?.length > 1"
                :offset="navigationHeight"
                :items="items ?? []"
            />
            <main>
                <BaseSection class="container-narrow">
                    <p
                        v-if="moduleItem?.introduction"
                        class="lead module__introduction"
                    >
                        {{ moduleItem?.introduction }}
                    </p>

                    <FlexibleSections
                        v-if="moduleItem?.contentSections?.length"
                        :sections="moduleItem.contentSections"
                    />
                </BaseSection>
            </main>

            <ClientOnly>
                <LazyTheModuleControls
                    :current-item="moduleItem"
                    :next-item="nextItem"
                    :next-text="nextText"
                    :back-item="previousItem"
                    :back-text="previousItem?.sections ? 'Go to start of chapter' : 'Previous item'"
                />
            </ClientOnly>
        </article>
    </div>
</template>
<script setup lang="ts">
import ModuleQuery from '~/graphql/queries/Module.graphql';
import ModuleItemQuery from '~/graphql/queries/ModuleItem.graphql';
import useModuleNavigation from '~/composables/useModuleNavigation';
import BaseTimeEstimate from '~/components/BaseTimeEstimate/BaseTimeEstimate.vue';
const isDesktop = useIsDesktop();

const navigationHeight = computed(() =>
    typeof document === 'undefined' ? 0 : document.querySelector('.uro-navigation')?.clientHeight || 0
);

definePageMeta({
    layout: 'module',
    pageTransition: {
        name: 'none',
    }
});

const t = useTranslation('components');

const vars = useCraftGraphqlSlug();
const { activeSite } = useMultisite();

const variables = computed(() => ({
    ...toValue(vars.variables),
    slug: ['module', ...Object.values(useRoute().params)].filter(Boolean),
    level: 3
}));


const { data: moduleDataRef } = await useLazyAsyncQuery({
    query: ModuleQuery,
    variables: { ...toValue(variables) }
});

const moduleData = computed(() => (toValue(moduleDataRef) as { module?: any })?.module);

const moduleItemResponse = await useLazyAsyncQuery({ query: ModuleItemQuery, variables });

const { craftEntry: moduleItem } = usePageQuery(
    moduleItemResponse,
    { entryKey: 'item', activeSite }
);

const { module } = useModule(moduleData);

const {nextItem, previousItem, items} = useModuleNavigation({ module });

const nextText = computed(() => {
    const next =  toValue(nextItem);
    const fallback = next?.__typename === 'module_chapter_Entry' ? 'chapter' : 'item';

    return next?.__typename !== 'module_end_Entry' ?
        `Next: ${ next?.title ?? fallback }` :
        'Complete module';
});

const chapter = computed(() => {
    return toValue(module)?.chapters.find((chap: any) => {
        return chap.sections.find(section => section.id === toValue(moduleItem)?.id);
    });
});

const chapterTitle = computed(() => toValue(chapter)?.title === toValue(moduleItem)?.title ?
    `Chapter ${ toValue(module)?.chapters?.findIndex(chap => chap.id === toValue(chapter)?.id) + 1 }` :
    toValue(chapter)?.title
);

const slug = useRoute().params?.module;
const { progress, initModule, moduleProgress } = useProgressStorage();
const { trackEvent } = useCustomGtmEvent();

onMounted(() => {
    if (!moduleData.value) {
        return;
    }

    const {
        id,
        title,
        chapters
    } = moduleData.value;

    if (!progress.value || progress.value?.modules?.find(entry => entry.slug !== slug)) {
        initModule(slug, id, title, chapters);
    }
});

onMounted(() => {
    if (!progress.value) {
        return;    
    }

    const title = progress.value.modules?.[0]?.title || null;

    trackEvent({
        event: 'module_view',
        'module_name': title || null,
        'module_progress': moduleProgress(slug) || null
    });
});
</script>
<style lang="less" scoped>
.layout__hero {
    background: var(--color-primary-500, #2453AF);
    margin-top: 0;
    padding-block: 1.875rem;

    p, span, div, h1, h2, h3, h4, h5, h6 {
        color: white;
    }

    @media @q-md-min {
        grid-column: 1 / -1 !important;  /* stylelint-disable-line declaration-no-important */
        width: 100% !important;  /* stylelint-disable-line declaration-no-important */

        padding-block: 3rem;
    }

}

section:first-child {
    margin-top: 0;
}

.module__introduction {
    margin-top: 1.5em;
}
</style>`;
